'use client';

import React, { useState, useMemo, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { ModuleRegistry } from 'ag-grid-community';
import { LicenseManager, AllEnterpriseModule } from 'ag-grid-enterprise';
import Icon from './Icon';

LicenseManager.setLicenseKey('Using_this_{AG_Grid}_Enterprise_key_{AG-119156}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{AIS_Portfolio_Services,_LLC}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{26_January_2027}____[v3]_[01]_MTgwMDkyMTYwMDAwMA==c34553d27345adb8f365b3cf7958f204');

ModuleRegistry.registerModules([AllEnterpriseModule]);

const enterpriseCSSFixes = `
  .ag-menu { background: #ffffff !important; box-shadow: 0 10px 40px rgba(0,0,0,0.15) !important; border-radius: 8px !important; }
  .ag-floating-filter, .ag-header-cell .ag-floating-filter-container { display: none !important; }
  .ag-header-cell-menu-button { opacity: 1 !important; background: rgba(255,255,255,0.9) !important; border-radius: 4px !important; }
  .ag-header-cell-menu-button:hover { background: #007bff !important; color: white !important; }
`;

const AgGrid = forwardRef(({
  rowData = [],
  columnDefs = [],
  gridOptions = {},
  pagination = false,
  paginationPageSize = 10,
  paginationPageSizeSelector = [10, 20, 50],
  enableSearch = false,
  enableSorting = true,
  enableFilter = true,
  height = '500px',
  headerHeight: headerHeightProp = 48,
  minHeight = '400px',
  theme = 'ag-theme-alpine',
  onGridReady,
  onRowClicked,
  onCellClicked,
  isClientUser,

  hideSelectAll = false,
  hidePivotMode = false,
  hideExcelExport = false,
  hideColumnMenu = false,
  hideExportButton = false,
  hideSideBar = false,

  customComponents = {},
  quickFilterText: externalFilter = '',
  onQuickFilterChange,
  searchPlaceholder = 'Search ...',

  // NEW: callback when filter/sort changes so parent can recalculate totals
  onFilterChanged,

  pinnedBottomRowData,  
  
  // Custom messages
  noRowsMessage = 'No records found',
  noFilteredRowsMessage = 'No matching records found',
}, ref) => {
  const [internalFilter, setInternalFilter] = useState('');
  const filter = externalFilter !== undefined ? externalFilter : internalFilter;
  
  const gridApiRef = useRef(null);
  const gridRef = useRef(null);
  const [showNoResultsOverlay, setShowNoResultsOverlay] = useState(false);
  const [filteredRowData, setFilteredRowData] = useState([]);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    getFilteredData: () => filteredRowData,
    getGridApi: () => gridApiRef.current,
    refreshData: () => {
      if (gridApiRef.current) {
        gridApiRef.current.refreshCells();
      }
    },
    // NEW: Get currently visible/displayed columns (after drag-drop removal)
    // added this function to drage and drop and get the remaining values.
    getVisibleColumns: () => {
      if (!gridApiRef.current) return [];
      const allDisplayedColumns = gridApiRef.current.getAllDisplayedColumns();
      return allDisplayedColumns
        .map((col) => {
          const colDef = col.getColDef();
          return {
            field: colDef.field,
            headerName: colDef.headerName || colDef.field,
            colId: col.getColId(),
          };
        })
        // Skip the row-selection checkbox column (no field)
        .filter((c) => c.field && c.field !== 'action');
    },
    // NEW: Get the filtered & sorted row data currently visible in grid
    getDisplayedRowData: () => {
      if (!gridApiRef.current) return [];
      const rows = [];
      gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
        if (node.data && !node.data.isFooter && !node.data.isTotalsRow) {
          rows.push(node.data);
        }
      });
      return rows;
    },
  }));

  const handleGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    if (onGridReady) onGridReady(params);
    
    // Set custom no rows template
    params.api.setGridOption('overlayNoRowsTemplate', getNoRowsTemplate());
    
    // Initialize filtered data
    const displayedRows = [];
    params.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) {
        displayedRows.push(node.data);
      }
    });
    setFilteredRowData(displayedRows);
  }, [onGridReady]);

  const getNoRowsTemplate = () => {
    const hasData = rowData?.length > 0;
    const message = hasData ? noFilteredRowsMessage : noRowsMessage;
    const icon = hasData && filter ? '🔍' : '📋';
    
    return `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 20px; text-align: center;">
        <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">${icon}</div>
        <div style="font-size: 16px; color: #6c757d; margin-bottom: 8px; font-weight: 500;">${message}</div>
        ${hasData && filter ? '<div style="font-size: 14px; color: #999;">Try adjusting your search criteria</div>' : ''}
      </div>
    `;
  };

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    if (onQuickFilterChange) {
      onQuickFilterChange(value);
    } else {
      setInternalFilter(value);
    }
  }, [onQuickFilterChange]);

  const handleClearSearch = useCallback(() => {
    if (onQuickFilterChange) {
      onQuickFilterChange('');
    } else {
      setInternalFilter('');
    }
  }, [onQuickFilterChange]);

  const handleExportExcel = useCallback(() => {
    if (!gridApiRef.current || hideExcelExport) return;
    gridApiRef.current.exportDataAsExcel({ fileName: 'data.xlsx' });
  }, [hideExcelExport]);

  // Handle filter changed event from AG Grid
  const handleFilterChanged = useCallback((params) => {
    const api = params.api;
    const displayedRows = [];
    
    // Get all displayed rows after filter and sort
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data && !node.data.isTotalsRow) {
        displayedRows.push(node.data);
      }
    });
    
    setFilteredRowData(displayedRows);
    
    // Call parent callback with filtered data
    if (onFilterChanged) {
      onFilterChanged({ 
        ...params, 
        filteredData: displayedRows,
        filteredCount: displayedRows.length,
        totalCount: rowData?.length || 0
      });
    }
  }, [onFilterChanged, rowData]);

  // Update overlay when filter or data changes
  useEffect(() => {
    if (!gridApiRef.current) return;
    
    const timer = setTimeout(() => {
      const api = gridApiRef.current;
      if (!api) return;
      
      const totalRows = rowData?.length || 0;
      let displayedRows = 0;
      
      try {
        displayedRows = api.getDisplayedRowCount();
      } catch (e) {
        displayedRows = totalRows;
      }
      
      // Update template with current state
      api.setGridOption('overlayNoRowsTemplate', getNoRowsTemplate());
      
      if (totalRows === 0 || (displayedRows === 0 && totalRows > 0)) {
        api.showNoRowsOverlay();
      } else {
        api.hideOverlay();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [filter, rowData]);

  // ✅ FIX: when the *shape* of columnDefs changes (e.g. adding/removing a
  // column group — like a second/third comparison job in Trend Analysis),
  // AG Grid's own React-prop diffing updates the columns but doesn't always
  // recompute the group header row's span/width for groups that already
  // existed before the change. That's what was causing an existing job's
  // merged header to visually break as soon as another job group was added.
  // Explicitly asking AG Grid to redraw its header after such a change fixes
  // it. Scoped to only fire when columnDefs actually contain groups
  // (`children`), so grids used elsewhere in the app with plain flat columns
  // are completely unaffected by this.
  useEffect(() => {
    if (!gridApiRef.current) return;
    const hasGroups = Array.isArray(columnDefs) && columnDefs.some((c) => Array.isArray(c?.children));
    if (!hasGroups) return;

    // Let AG Grid finish applying the new columnDefs first, then force a
    // header layout recalculation on the next tick.
    const timer = setTimeout(() => {
      try {
        // Re-assert the header row heights before refreshing — belt-and-
        // suspenders alongside the headerHeight/groupHeaderHeight props
        // below, in case a dynamic column-structure change (2nd/3rd job
        // group added) leaves AG Grid's internal group-row height stale.
        gridApiRef.current?.setGridOption('headerHeight', headerHeightProp);
        gridApiRef.current?.setGridOption('groupHeaderHeight', headerHeightProp);
        gridApiRef.current?.refreshHeader();
      } catch (e) {
        // grid may be mid-teardown — safe to ignore
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [columnDefs]);

  const checkboxColumn = useMemo(() => {
    if (hideSelectAll) return null;
    return {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      width: 50,
      pinned: 'left',
      sortable: false,
      filter: false,
      lockPosition: true,
      suppressMenu: true,
      suppressHeaderMenuButton: true,
    };
  }, [hideSelectAll]);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    resizable: true,
    sortable: enableSorting,
    filter: enableFilter ? 'agSetColumnFilter' : false,
    enableRowGroup: !hidePivotMode,
    enablePivot: !hidePivotMode,
    enableValue: !hidePivotMode,
    floatingFilter: false,
    suppressHeaderMenuButton: hideColumnMenu,
    suppressHeaderContextMenu: hideColumnMenu,
    valueGetter: (params) => {
      if (!params.data) return null;
      if (params.colDef.field && params.data[params.colDef.field] !== undefined) {
        return params.data[params.colDef.field];
      }
      const data = params.data.originalData || params.data;
      return data?.[params.colDef.field] ?? params.value ?? '';
    },
  }), [enableSorting, enableFilter, hidePivotMode, hideColumnMenu]);

  const finalColumnDefs = useMemo(() => {
    if (checkboxColumn) {
      return [checkboxColumn, ...columnDefs];
    }
    return columnDefs;
  }, [columnDefs, checkboxColumn]);

  const sideBar = useMemo(() => {
    if (hideSideBar) return false;
    if (hidePivotMode) {
      return {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressPivotMode: true,
              suppressValues: true,
              suppressRowGroups: true,
            }
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel'
          },
        ],
      };
    }
    return {
      toolPanels: [
        { id: 'columns', labelDefault: 'Columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
        { id: 'filters', labelDefault: 'Filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
      ],
    };
  }, [hidePivotMode, hideSideBar]);

  const gridOptionsFixed = useMemo(() => {
    const baseOptions = {
      ...gridOptions,
      theme: 'legacy',
      suppressRowHoverHighlight: false,
      suppressDragLeaveHidesColumns: false,
      suppressColumnMoveAnimation: true,
      suppressContextMenu: hideColumnMenu,
    };

    if (hideExcelExport || hideColumnMenu) {
      baseOptions.getContextMenuItems = () => {
        return ['copy', 'paste', 'separator'].filter(item =>
          item !== 'export' && item !== 'exportExcel' && 
          item !== 'exportDataAsCsv' && item !== 'exportDataAsExcel'
        );
      };
    }

    return baseOptions;
  }, [gridOptions, hideExcelExport, hideColumnMenu]);

  return (
    <>
      <style>{enterpriseCSSFixes}</style>
      
      <style>{`
        .ag-overlay-no-rows-wrapper {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ag-overlay-no-rows-center {
          text-align: center;
        }
      `}</style>

      <div className="ag-grid-container">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #dee2e6',
          borderRadius: '6px 6px 0 0',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 600 }}></div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {enableSearch && (
              <div style={{ position: 'relative', width: '300px', marginBottom: '10px' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 1,
                }}>
                  <Icon name="search" alt="search-icon" size={14} />
                </div>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={filter}
                  onChange={handleSearchChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 36px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    color: '#6E7581',
                    outline: 'none',
                    fontSize: '14px',
                  }}
                />
                {filter && (
                  <button
                    onClick={handleClearSearch}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      fontSize: '18px',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '0',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      zIndex: 1,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={`${theme}`} style={{ height, width: '100%', minHeight }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={finalColumnDefs}
            defaultColDef={defaultColDef}
            gridOptions={gridOptionsFixed}
            sideBar={sideBar}
            rowSelection={hideSelectAll ? undefined : "multiple"}
            suppressRowClickSelection={true}
            suppressMenuHide={true}
            pagination={pagination}
            pinnedBottomRowData={pinnedBottomRowData}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            animateRows={true}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            rowHeight={52}
            headerHeight={headerHeightProp}
            groupHeaderHeight={headerHeightProp}
            onGridReady={handleGridReady}
            onRowClicked={onRowClicked}
            onCellClicked={onCellClicked}
            onFilterChanged={handleFilterChanged}
            quickFilterText={filter}
            suppressCsvExport={hideExcelExport}
            suppressExcelExport={hideExcelExport}
            {...customComponents}
          />
        </div>
      </div>
    </>
  );
});

AgGrid.displayName = 'AgGrid';

export const StarIcon = ({ isFavorite, onStarClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onStarClick?.();
    }}
    style={{
      color: isFavorite ? '#ffc107' : '#e4e5e9',
      fontSize: '20px',
      cursor: onStarClick ? 'pointer' : 'default',
    }}
  >
    {isFavorite ? '★' : '☆'}
  </span>
);

export const ClickableCell = ({ value, onClick, style = {} }) => (
  <span
    style={{
      color: '#007bff',
      cursor: 'pointer',
      textDecoration: 'underline',
      fontWeight: 500,
      ...style,
    }}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
  >
    {value}
  </span>
);

export const StatusCell = ({ value, activeIcon = '../assets/active-icon.png' }) => {
  const isActive = value === 'Active';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {isActive && (
        <img
          src={activeIcon}
          alt="Active"
          style={{ width: '16px', height: '16px', objectFit: 'contain' }}
        />
      )}
      <span style={{ color: isActive ? '#28a745' : '#6c757d' }}>
        {value}
      </span>
    </div>
  );
};

export default AgGrid;
